import './style.scss'
import '../avatar-market/style.scss'
import { Button, Modal } from 'antd'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import * as Api from '@/api/home'
import IconFilter from '@/assets/icon-filter.png'
import CheckLogin from '@/components/CheckLogin'
import { eventTracking, Render } from '@/libs/util'
import MarketModal from './components/market-modal'

const AvatarMarket: FC = () => {
  const { t: translate } = useTranslation(['avatar_market', 'system'])
  const [data, setData] = useState<any[]>([])
  const [marketModalOpen, setMarketModalOpen] = useState(false)
  const [marketDetail, setMarketDetail] = useState<any>()
  const [tagList, setTagList] = useState<any[]>([])
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState<any>({
    tags: [],
    pay: 0,
    sold: 0
  })
  const [popupFilters, setPopupFilters] = useState<any>({})

  const showData = useMemo(() => {
    return data
  }, [data])

  useEffect(() => {
    eventTracking('DigitalStore')
    getTags()
  }, [])

  useEffect(() => {
    if (filters) {
      getData()
    }
  }, [filters])

  const getData = async () => {
    const { tags, sold, pay } = filters
    const tagStr = tags.map((t: string) => `tags=${t}`).join('&')

    const [merchandises] = await Promise.all([
      Api.getMerchandisesV3(`?pay_requirement=${pay}&sold_status=${pay === 1 ? 0 : sold}&${tagStr}`)
    ])
    setData(merchandises?.list || [])
  }

  const getTags = async () => {
    const res = await Api.getMerchandiseTags()
    setTagList(res?.tags || [])
  }

  const onFiltersChange = (key: string, value: any) => {
    setPopupFilters({
      ...popupFilters,
      [key]: value
    })
  }

  const resetFilters = () => {
    setPopupFilters({
      tags: [],
      pay: 0,
      sold: 0
    })
  }

  const onTagChange = (t: string) => {
    const pt = popupFilters.tags || []
    if (pt?.includes(t)) {
      onFiltersChange(
        'tags',
        pt.filter((p: any) => p !== t)
      )
    } else {
      onFiltersChange('tags', [...pt, t])
    }
  }

  const onMarketClick = (data: any) => {
    eventTracking('DigitalStoreClick', {
      id: data.id,
      name: data.name
    })
    setMarketDetail(data)
    setMarketModalOpen(true)
  }

  const onCancel = () => {
    getData()
    setMarketDetail(undefined)
    setMarketModalOpen(false)
  }

  return (
    <div className="page-common page-avatar-market">
      <div className="common-header">{translate('avatar_market.title')}</div>

      <div className="common-wrapper">
        <div className="nav">
          <div
            className="filter-box"
            onClick={() => {
              setPopupFilters(filters)
              setFilterOpen(true)
            }}
          >
            <span className="gradient">{translate('system.filter')}</span>
            <img className="icon" src={IconFilter} />
          </div>
          <a
            target="_blank"
            className="link gradient"
            href="https://lingverse.feishu.cn/share/base/form/shrcnNx9YWvGeYFc7DEF20CrAAW"
            rel="noreferrer"
          >
            {translate('avatar_market.custom_order')}
          </a>
        </div>

        <div className="page-container">
          {showData.map((d) => (
            <CheckLogin key={`${d.id}${d.name}`}>
              <Render>
                {function LazyImage() {
                  const [ref, inView] = useInView({ triggerOnce: true })

                  return (
                    <div className="list-image-box" key={`${d.id}${d.name}`} onClick={() => onMarketClick(d)}>
                      <div className="box-main">
                        <div className="image" ref={ref}>
                          {inView && <img src={d.covers?.[0]} className={`${d.name}`} />}

                          {d.status === 2 && (
                            <div className="mask">
                              <span className="text">{translate('avatar_market.hired')}</span>
                            </div>
                          )}
                        </div>

                        <div className="name">
                          <span className="ellipsis">{d.name}</span>
                        </div>
                        <div className="bottom">
                          {d.type === 3 ? (
                            <div className="price gradient">{translate('system.free')}</div>
                          ) : (
                            <div className="price ellipsis">
                              <label>{d.price || d.starting_price}</label>
                              <label className="credits">{translate('system.points')}</label>
                            </div>
                          )}
                          <span className="num">
                            <label className="gradient">
                              {translate('avatar_market.avatar_count', { count: d.videos?.length || 0 })}
                            </label>
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                }}
              </Render>
            </CheckLogin>
          ))}
        </div>
      </div>

      <Modal
        className="title-modal filter-modal"
        title={translate('avatar_market.filter_title')}
        open={filterOpen}
        width={580}
        maskClosable={true}
        footer={
          <div className="filter-footer-confirm">
            <Button type="default" onClick={resetFilters}>
              {translate('system.reset')}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setFilters(popupFilters)
                setFilterOpen(false)
              }}
            >
              {translate('system.confirm')}
            </Button>
          </div>
        }
        onCancel={() => setFilterOpen(false)}
      >
        <div className="filter-fragment">
          <div className="title">{translate('avatar_market.payment_status')}</div>
          <div className="filte-list">
            <div
              className={`filter-item w50 ${popupFilters.pay === 1 ? 'actived' : ''}`}
              onClick={() => onFiltersChange('pay', popupFilters.pay === 1 ? 0 : 1)}
            >
              <label>{translate('system.free')}</label>
            </div>
            <div
              className={`filter-item w50 ${popupFilters.pay === 2 ? 'actived' : ''}`}
              onClick={() => onFiltersChange('pay', popupFilters.pay === 2 ? 0 : 2)}
            >
              <label>{translate('avatar_market.paid')}</label>
            </div>
          </div>
        </div>
        <div className="filter-fragment">
          <div className="title">{translate('system.type')}</div>
          <div className="filte-list">
            {tagList.map((t) => (
              <div
                className={`filter-item ${popupFilters?.tags?.includes(t) ? 'actived' : ''}`}
                key={t}
                onClick={onTagChange.bind(this, t)}
              >
                <label>{t}</label>
              </div>
            ))}
          </div>
        </div>
        <div className="filter-fragment">
          <div className="title">{translate('avatar_market.hire_status')}</div>
          <div className="filte-list">
            <div
              className={`filter-item w50 ${popupFilters.sold === 1 ? 'actived' : ''}`}
              onClick={() => onFiltersChange('sold', popupFilters.sold === 1 ? 0 : 1)}
            >
              <label>{translate('avatar_market.not_hired')}</label>
            </div>
            <div
              className={`filter-item w50 ${popupFilters.sold === 2 ? 'actived' : ''}`}
              onClick={() => onFiltersChange('sold', popupFilters.sold === 2 ? 0 : 2)}
            >
              <label>{translate('avatar_market.hired')}</label>
            </div>
          </div>
        </div>
      </Modal>

      <MarketModal open={marketModalOpen} marketDetail={marketDetail} onCancel={onCancel} />
    </div>
  )
}

export default memo(AvatarMarket)
